import React from "react"
import { Radio, Tooltip } from "antd"
import { ChevronRightIcon, MapPinIcon } from "@heroicons/react/24/outline"
import { getStaticMapUrl } from "components/GeoPicker"

// ─── Types ────────────────────────────────────────────────────────────────────

export type SetupWorkplace = {
  id: string
  name: string
  description: string
  address: string | null
  city?: string | null
  country?: string | null
  state?: string | null
  taxOfficeName?: string | null
  taxOfficeCode?: string | null
  nhfOfficeName?: string | null
  nhfOfficeCode?: string | null
  latitude?: number | null
  longitude?: number | null
  workplaceConfig?: object | null
}

interface Props {
  workplaces: SetupWorkplace[]
  selectedWorkplaceId: string
  onSelect: (workplaceId: string) => void
  onConfigureAddress?: (workplace: SetupWorkplace) => void
  onEditWorkplace: (selectedWorkplace: SetupWorkplace) => void
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function truncateAddress(
  address: string | null,
  maxLength = 50
): string | null {
  if (address == null || address.length <= maxLength) return address
  return address.slice(0, maxLength).trim() + "..."
}

// ─── Component ────────────────────────────────────────────────────────────────

const SetupWorkplaceList: React.FC<Props> = ({
  workplaces,
  selectedWorkplaceId,
  onSelect,
  onConfigureAddress,
  onEditWorkplace,
}) => (
  <div className="flex flex-col gap-3">
    {workplaces.map((workplace) => {
      const isSelected = workplace.id === selectedWorkplaceId
      const hasCoordinates =
        workplace.latitude != null && workplace.longitude != null

      return (
        <div
          key={workplace.id}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(workplace.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onSelect(workplace.id)
          }}
          className={`relative flex rounded-xl h-[118px] justify-center items-center border overflow-hidden cursor-pointer transition bg-clip-padding ${
            hasCoordinates
              ? "bg-no-repeat bg-cover bg-right"
              : "bg-no-repeat bg-cover bg-right"
          } ${
            isSelected
              ? "border-[#004AF5]"
              : "border-[#E2E8F0] hover:border-[#d0d3d7]"
          }`}
          style={
            hasCoordinates
              ? {
                  backgroundImage: `url("${getStaticMapUrl(
                    workplace.latitude!,
                    workplace.longitude!
                  )}")`,
                }
              : {
                  backgroundImage:
                    'url("https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/7e7c323c-3ab0-4b3f-a9c9-2f32b36b3b34.svg")',
                }
          }
        >
          {hasCoordinates && (
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-4/5 z-0"
              style={{
                background:
                  "linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 60%, rgba(255,255,255,0) 100%)",
              }}
            />
          )}

          <div className="relative z-10 flex flex-1 items-center gap-2 px-4 py-6">
            <Radio
              checked={isSelected}
              onChange={() => onSelect(workplace.id)}
            />

            <div className="flex flex-col gap-1">
              <p className="font-bold capitalize text-lg text-[#0F172B]">
                {workplace.name}
              </p>

              <p className="text-sm text-[#0F172B] flex items-center flex-wrap gap-1">
                <span className="inline-flex items-center gap-1">
                  <MapPinIcon className="w-4 h-4 text-slate-900" />
                  <span className="flex items-center gap-1">
                    <span className="font-bold">{workplace.description}</span>
                    {workplace.address && (
                      <Tooltip title={workplace.address}>
                        <span
                          className="text-[#0F172B] capitalize flex items-center gap-2"
                          // onClick={() => onEditWorkplace(workplace)}
                        >
                          {" "}
                          {truncateAddress(workplace.address)}
                          <button
                            type="button"
                            aria-label="Edit workplace"
                            onClick={(e) => {
                              e.stopPropagation()
                              onEditWorkplace(workplace)
                            }}
                            className="p-2 -m-2 inline-flex items-center justify-center rounded hover:bg-slate-100 hover:text-blue-700"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g clipPath="url(#clip0_7059_1680)">
                                <path
                                  d="M5.79313 13.5001H3C2.86739 13.5001 2.74021 13.4474 2.64645 13.3536C2.55268 13.2599 2.5 13.1327 2.5 13.0001V10.207C2.50006 10.0745 2.55266 9.94753 2.64625 9.85383L10.3538 2.14633C10.4475 2.05263 10.5746 2 10.7072 2C10.8397 2 10.9669 2.05263 11.0606 2.14633L13.8538 4.93758C13.9474 5.03134 14.0001 5.15847 14.0001 5.29102C14.0001 5.42357 13.9474 5.5507 13.8538 5.64446L6.14625 13.3538C6.05255 13.4474 5.92556 13.5 5.79313 13.5001Z"
                                  stroke="#90A1B9"
                                  strokeWidth="1.4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M8.5 4L12 7.5"
                                  stroke="#90A1B9"
                                  strokeWidth="1.4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                              <defs>
                                <clipPath id="clip0_7059_1680">
                                  <rect
                                    width="16"
                                    height="16"
                                    fill="white"
                                  />
                                </clipPath>
                              </defs>
                            </svg>
                          </button>
                        </span>
                      </Tooltip>
                    )}
                  </span>
                </span>

                {!hasCoordinates && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation()
                      onConfigureAddress?.(workplace)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        e.stopPropagation()
                        onConfigureAddress?.(workplace)
                      }
                    }}
                    className="ml-1 inline-flex items-center gap-1 font-semibold text-[#004AF5] cursor-pointer hover:underline"
                  >
                    Configure address.
                    <ChevronRightIcon className="w-3 h-3" />
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )
    })}
  </div>
)

export default SetupWorkplaceList
